// Utility functions for generating structured data (schema.org) markup
import { createPageTitle } from "./seo";

type OrganizationSchema = {
  "@context": "https://schema.org";
  "@type": "Organization";
  name: string;
  url: string;
  logo: string;
  description: string;
  sameAs?: string[];
  contactPoint?: {
    "@type": "ContactPoint";
    contactType: string;
    url: string;
  };
  foundingDate?: string;
  founders?: {
    "@type": "Person";
    name: string;
  }[];
};

type WebSiteSchema = {
  "@context": "https://schema.org";
  "@type": "WebSite";
  name: string;
  url: string;
  description: string;
  publisher: {
    "@type": "Organization";
    name: string;
    logo: string;
  };
  potentialAction?: {
    "@type": "SearchAction";
    target: {
      "@type": "EntryPoint";
      urlTemplate: string;
    };
    "query-input": string;
  };
};

type WebPageSchema = {
  "@context": "https://schema.org";
  "@type": "WebPage";
  name: string;
  url: string;
  description: string;
  isPartOf: {
    "@type": "WebSite";
    name: string;
    url: string;
  };
  about?: {
    "@type": "Thing";
    name: string;
  };
  mainEntity?: {
    "@type": "SoftwareApplication";
    name: string;
    applicationCategory: string;
    description: string;
  };
};

// Generate Organization schema
export function createOrganizationSchema(): OrganizationSchema {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "andocs",
    url: "https://andocs.cz",
    logo: "https://andocs.cz/logo.png",
    description:
      "All-in-one integration platform that enables AI solutions to securely access data from diverse sources through standardized Model Context Protocol (MCP) and modern authentication protocols.",
    sameAs: [
      "https://github.com/blogic/andocs",
      "https://twitter.com/blogictemplate",
      "https://linkedin.com/company/andocs",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      url: "https://andocs.cz/contact",
    },
    foundingDate: "2024",
  };
}

// Generate WebSite schema with search functionality
export function createWebSiteSchema(): WebSiteSchema {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "andocs",
    url: "https://andocs.cz",
    description:
      "Connect your AI solution to data with built-in authentication. andocs enables secure access to diverse data sources through MCP and modern authentication protocols.",
    publisher: {
      "@type": "Organization",
      name: "andocs",
      logo: "https://andocs.cz/logo.png",
    },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate:
          "https://andocs.cz/docs?q={search_term_string}",
      },
      "query-input": "required name=search_term_string",
    },
  };
}

// Generate WebPage schema
export function createWebPageSchema(
  url: string,
  description: string,
  about?: string,
  ...titleParts: string[]
): WebPageSchema {
  const name = createPageTitle(...titleParts);

  const schema: WebPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name,
    url,
    description,
    isPartOf: {
      "@type": "WebSite",
      name: "andocs",
      url: "https://andocs.cz",
    },
  };

  if (about) {
    schema.about = {
      "@type": "Thing",
      name: about,
    };
  }

  return schema;
}
